/**
 * 多版本菜单 N35-B5（老扫码站，owner Q21 / Q22 / Q26，plan §3.1、§3.3、§3.8）：点单链接码 —— 「地址里有没有链接码、是哪一个」全站只问这里。
 *
 * 管理端 Ordering links 卡给附加菜单签发的地址是「原地址 + `&menuLink=<24 位小写十六进制>`」（Counter 码 / 单桌码 / 通用桌码 / 外带码都可以带）。
 * 扫码顾客持码进来，读菜单、下单、「菜单刚更新」后的重新解析都在 context 里带上这个码（`qrEntry.js menuEntry` → `qrMenu.js buildQrMenuContext`），
 * 后端（`MenuOrderingLinkService`）认得就强制那份 Live 菜单、不看条件（Q21）；认不得 / 停用 / 菜单不在线就当没带，照常是平常菜单（Q22）。
 * 同一个地址同时带预览码：照预览处理（Q26，后端预览优先；本地拦单 `previewOrderBlock.js` 只看预览码，不变）。合单不带（`menuEntry` 回 `{}`）。
 *
 * ## 零存储（照 `previewMenu.js`，plan §3.3 / §3.12 第 8 条）
 * 码**只从当前地址现读**：`window.location.search`，以及导航进行中还没写进地址栏的那一下用路由 query 兜着。
 * 绝不写进 Vuex（根 state 整份落 `localStorage.vuex`）、localStorage、sessionStorage、cookie、菜单缓存键或别的任何地方：
 * 码代表「这一次是从哪张码进来的」，存下来就会在同一台手机下一次扫普通码时还强制那份菜单。
 * 站内跳转靠路由守卫把上一页地址里的码补到下一页（`carryMenuLink`，经 `menuAddress.js` 排在所有守卫之前）——读的是上一页的地址，不是存储。
 * 地址里丢了码（整页跳到后端拼的地址等）只会回到平常菜单 + 「菜单刚更新」弹窗（安全方向）。
 *
 * ## 与预览码不串
 * 键名不同（`menuLink` / `previewMenu`），码长不同（链接 24 位、预览 32 位）：任何一方塞进另一方的位置都只会「认不出」。
 * 链接码**不进** `X-Preview-Menu` 请求头（那个头只认预览码，`previewRequest.js` 只问 `currentPreviewMenuToken`）。
 *
 * 本文件是纯函数（只读 `window.location`），不 import 任何别的模块。
 */

/** 地址与 context 里的键（后端 `MenuOrderingLinkService::CONTEXT_KEY`） */
export const MENU_LINK_QUERY_KEY = 'menuLink'

/**
 * 后端签发的形状：`bin2hex(random_bytes(12))`，后端判形状用 `MenuOrderingLinkService::CODE_PATTERN` = `/^[0-9a-f]{24}$/D`。
 * JS 的 `$` 不带 m 标志时本来就是严格结尾，与 PHP 加 D 等价。改形状两侧一起改（测试里有手抄镜像）。
 */
export const MENU_LINK_CODE_PATTERN = /^[0-9a-f]{24}$/

/** 一个值是不是合法链接码：是 → 原样返回；数组 / 非字符串 / 形状不对 → null */
export function normalizeMenuLinkCode(v) {
  return typeof v === 'string' && MENU_LINK_CODE_PATTERN.test(v) ? v : null
}

/** 一个 query 值（重复参数时 vue-router 给数组）里第一个形状合法的码，没有 → null */
export function firstMenuLinkCode(v) {
  const values = Array.isArray(v) ? v : [v]
  for (let i = 0; i < values.length; i++) {
    const code = normalizeMenuLinkCode(values[i])
    if (code !== null) return code
  }
  return null
}

/** 从 query string（如 `location.search`）取码：任何一个值合法就取第一个合法的 */
export function menuLinkCodeFromSearch(search) {
  if (typeof search !== 'string') return null
  try {
    return firstMenuLinkCode(new URLSearchParams(search).getAll(MENU_LINK_QUERY_KEY))
  } catch (e) {
    return null
  }
}

/**
 * 当前页面的链接码，没有 → null。每次现读：先看地址栏，再看调用方给的路由 query（导航进行中、history 还没写的那一下，
 * 或调用方手上只有 `$route`）。两处都只是地址，不是存储。
 * @param {Object} [routeQuery] `this.$route.query`
 */
export function currentMenuLinkCode(routeQuery) {
  const search = typeof window !== 'undefined' && window.location ? window.location.search : ''
  const fromAddress = menuLinkCodeFromSearch(search)
  if (fromAddress !== null) return fromAddress
  return routeQuery && typeof routeQuery === 'object' ? firstMenuLinkCode(routeQuery[MENU_LINK_QUERY_KEY]) : null
}

/**
 * 路由守卫用（与 `carryPreviewMenu` 同口径）：上一页地址里有合法码、下一页没有**单个合法码**（没带 / 空串 / 坏码 / 重复键）⇒
 * 返回补上码的下一页地址（别的参数与 hash 原样），否则 null（放行）。补过之后再判一次一定是 null，不会死循环。
 * 下一页自己带着合法码时不动。
 */
export function carryMenuLink(to, from) {
  const code = firstMenuLinkCode(from && from.query ? from.query[MENU_LINK_QUERY_KEY] : undefined)
  if (code === null) return null
  const query = (to && to.query) || {}
  if (normalizeMenuLinkCode(query[MENU_LINK_QUERY_KEY]) !== null) return null
  return { path: to.path, query: { ...query, [MENU_LINK_QUERY_KEY]: code }, hash: to.hash || '' }
}
